import {
  ConversationMessageMetadata,
  LLMModels,
} from "@shaarp/common";
import { ChatStatus, UIMessage } from "ai";
import type { MemoryMessageToSend } from "./chat-conversation";

/**
 * Builds a transient user message rendered optimistically while a brand-new
 * conversation is being created and the first answer is awaited. Mirrors the
 * shape produced by the live stream so the bubble doesn't shift when the real
 * message takes over. Attached files are omitted (they appear once uploaded).
 */
export function buildOptimisticUserMessage(
  message: MemoryMessageToSend,
  userId: string | null
): UIMessage<ConversationMessageMetadata> {
  return {
    id: "optimistic-user",
    role: "user",
    parts: [{ type: "text", text: message.text }],
    metadata: {
      createdAt: new Date(),
      llmId: message.llmId as LLMModels,
      userId,
      files: [],
      forcedTool: message.forcedTool ?? undefined,
      requestMode: message.requestMode ?? undefined,
      companyContext: message.companyContext
        ? {
            id: message.companyContext.id,
            name: message.companyContext.name,
            domain: message.companyContext.domain,
            logo: message.companyContext.logo,
            thumbnails: message.companyContext.thumbnails,
          }
        : undefined,
      prompt: message.prompt,
    },
  };
}

/**
 * The status the thread shows while the first message of a new conversation
 * stands in optimistically: "submitted" — a turn under way, so the thinking
 * placeholder spins and the composer offers stop — until the stream says
 * otherwise or the turn is known to have failed.
 *
 * Failed covers two cases. A send the server refused comes back as an inline
 * error. A send that failed before it ever left the browser — an upload that
 * did not go through — never reaches `sendMessage`, so nothing would ever lift
 * the forced status: the composer stayed in stop, its field disabled over the
 * draft just handed back, until a reload.
 */
export function resolveOptimisticStatus({
  status,
  isShowingOptimistic,
  hasInlineError,
  hasSendFailed,
}: {
  status: ChatStatus;
  isShowingOptimistic: boolean;
  hasInlineError: boolean;
  hasSendFailed: boolean;
}): ChatStatus {
  return isShowingOptimistic && !hasInlineError && !hasSendFailed
    ? "submitted"
    : status;
}
